// @ts-nocheck
import fieldSizeToButtonSize from "@store/Framework/Common/Utilities/fieldSizeToButtonSize";
import FlexWrapper, {
    flexWrapperGapStyles,
    FlexWrapperPresentationProps,
} from "@store/Library/Components/Toolkit/FlexWrapper";
import { FormFieldSizeVariant } from "@store/MobiusOverrides/FormField/FormField";
/* Store Custom Code
import ProductAddToCartButton, {
    ProductAddToCartButtonProps,
    useShowProductAddToCartButton,
} from "@store/Overrides/Components/ProductAddToCartButton";
*/
import ProductQuantityOrdered, {
    ProductQuantityOrderedProps,
    useShowProductQuantityOrdered,
} from "@store/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect, {
    ProductUnitOfMeasureSelectProps,
    useShowProductUnitOfMeasureSelect,
} from "@store/Overrides/Components/ProductUnitOfMeasureSelect";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { ButtonSizeVariant } from "@toolkit/Framework/Types/Button";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import Button from "@store/MobiusOverrides/Button/Button";
import { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Props";
import ProductAddToCartButton, {
    ProductAddToCartButtonProps,
    useShowProductAddToCartButton,
} from "@store/Overrides/Components/ProductAddToCartButton";
/* Store Custom Code - End */

type ExtendedProps<T> = T & {
    "data-test-selector"?: string;
};

export interface AddToCartOptionsAndButtonProps extends ExpandStyles<AddToCartOptionsAndButtonStyles> {
    sizeVariant?: FormFieldSizeVariant;
    buttonSizeVariant?: ButtonSizeVariant;
    showUnitOfMeasure?: boolean;
    // Store Custom Code
    analyticsId?: string;
}

export interface AddToCartOptionsAndButtonStyles {
    flexWrapper?: FlexWrapperPresentationProps;
    unitOfMeasure?: ExtendedProps<ProductUnitOfMeasureSelectProps>;
    quantityOrdered?: ExtendedProps<ProductQuantityOrderedProps>;
    addToCartButton?: ExtendedProps<ProductAddToCartButtonProps>;
    // Store Custom Code
    buttonViewMoreOptions?: ButtonPresentationProps;
}

export const addToCartOptionsAndButtonStyles: AddToCartOptionsAndButtonStyles = {
    flexWrapper: {
        gap: pxToRem(10),
    },
    unitOfMeasure: {
        extendedStyles: {
            cssOverrides: {
                formField: css`
                    flex: 1 1 50%;
                    width: auto;
                `,
            },
        },
    },
    addToCartButton: {
        extendedStyles: {
            button: {
                css: css`
                    flex: 1 0 auto;
                    align-self: flex-end;
                `,
            },
        },
    },
    // Store Custom Code
    buttonViewMoreOptions: {
        variant: "secondary",
        buttonType: "outline",
        css: css`
            ${x => x.theme.button.secondary.css}
            min-width: 100%;
            margin-bottom: ${pxToRem(34.25)};
        `,
    },
};

const AddToCartOptionsAndButton: FC<AddToCartOptionsAndButtonProps & HasProductContext /* Store Custom Code */> = ({
    extendedStyles,
    overridedStyles,
    sizeVariant = "small",
    buttonSizeVariant: baseButtonSizeVariant,
    showUnitOfMeasure = true,
    // Store Custom Code
    analyticsId,
    productContext,
}) => {
    const history = useHistory(); // Store Custom Code
    const baseStyles = useExpandProps(addToCartOptionsAndButtonStyles, extendedStyles, overridedStyles);
    const styles = useMemo(() => {
        const buttonSizeVariant = baseButtonSizeVariant || fieldSizeToButtonSize(sizeVariant);
        const itemCss = flexWrapperGapStyles(baseStyles.flexWrapper?.gap, baseStyles.flexWrapper?.verticalGap);

        return mergeProps(baseStyles, {
            unitOfMeasure: {
                extendedStyles: {
                    sizeVariant,
                    cssOverrides: {
                        formField: itemCss,
                    },
                },
            },
            quantityOrdered: {
                extendedStyles: {
                    sizeVariant,
                    cssOverrides: {
                        formField: itemCss,
                    },
                },
            },
            addToCartButton: {
                extendedStyles: {
                    button: {
                        sizeVariant: buttonSizeVariant,
                        css: itemCss,
                    },
                },
            },
        } as AddToCartOptionsAndButtonStyles);
    }, [baseStyles, sizeVariant]);

    const showProductAddToCartButton = useShowProductAddToCartButton(
        extendedStyles?.quantityOrdered?.configurationCompleted,
        extendedStyles?.quantityOrdered?.variantSelectionCompleted,
    );
    const showProductQuantityOrdered = useShowProductQuantityOrdered(
        extendedStyles?.quantityOrdered?.configurationCompleted,
        extendedStyles?.quantityOrdered?.variantSelectionCompleted,
    );
    const showProductUnitOfMeasureSelect = useShowProductUnitOfMeasureSelect();

    if (!showProductAddToCartButton && !showProductQuantityOrdered && !showProductUnitOfMeasureSelect) {
        /* Store Custom Code - Start */
        const { product } = productContext;
        if (product.variantTraits && product.variantTraits.length > 0) {
            const productDetailPath: string =
                "productInfo" in productContext
                    ? productContext.productInfo.productDetailPath
                    : "productUri" in product
                    ? (product.productUri as string)
                    : "";
            return (
                <Button
                    {...styles.buttonViewMoreOptions}
                    onClick={() => {
                        if (productDetailPath.startsWith("http")) {
                            window.location.href = productDetailPath;
                        } else {
                            history.push(productDetailPath);
                        }
                    }}
                >
                    {translate("View More Options")}
                </Button>
            );
        }
        /* Store Custom Code - End */
        return null;
    }

    return (
        <FlexWrapper wrap={false} {...styles.flexWrapper}>
            {showUnitOfMeasure && <ProductUnitOfMeasureSelect {...styles.unitOfMeasure} />}
            <ProductQuantityOrdered {...styles.quantityOrdered} />
            <ProductAddToCartButton {...styles.addToCartButton} analyticsId={analyticsId} /* Store Custom Code */ />
        </FlexWrapper>
    );
};

export default withProductContext(AddToCartOptionsAndButton); // Store Custom Code
